/* eslint-disable @typescript-eslint/no-unused-vars */
import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import {
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  Typography
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { useEffect, useState } from 'react'

import ActiveTabs from '../../components/ActiveTabs'
import { getTranslate } from '../../localization'
import { EntityType } from '../../types/reserveMonitoringList'
import {
  EntityTypeForRoleManagement,
  EntityTypes,
  Permission,
  RoleDetails
} from '../../types/role'
import useStyles from './styles/filters.styles'

interface RoleBasicInfoProps {
  temporaryRoleDetails: RoleDetails
  setTemporaryRoleDetails: React.Dispatch<React.SetStateAction<RoleDetails>>
  setfilteredPermissions: React.Dispatch<React.SetStateAction<Permission[]>>
  // The entity types the user may create roles of, from IAM. Defaults to all of them.
  entityTypeOptions?: { label: string; value: EntityTypeForRoleManagement }[]
  // A role the user may view but not change.
  readOnly?: boolean
}

const RoleBasicInfo: React.FC<RoleBasicInfoProps> = ({
  temporaryRoleDetails,
  setTemporaryRoleDetails,
  setfilteredPermissions,
  entityTypeOptions = EntityTypes,
  readOnly = false
}) => {
  const classes = useStyles()
  const [searchInputVal, setSearchInputVal] = useState('')
  const [activeStatus, setaAtiveStatus] = useState('All')

  useEffect(() => {
    const searchLower = searchInputVal?.toLowerCase() ?? ''

    const result = temporaryRoleDetails.permissions
      .map((perm) => {
        const permissionMatches = perm.permissionName
          .toLowerCase()
          .includes(searchLower)

        const matchedSubPermissions = perm.subPermissions.filter((sub) =>
          sub.name.toLowerCase().includes(searchLower)
        )

        // If nothing matches → skip permission
        if (!permissionMatches && matchedSubPermissions.length === 0) {
          return null
        }

        return {
          ...perm,
          subPermissions: matchedSubPermissions
        }
      })
      .filter((p): p is Permission => Boolean(p))
      .map((perm) => {
        if (activeStatus === 'All') return perm

        const filteredSubPermissions = perm.subPermissions.filter((sub) => {
          if (activeStatus === 'Active') {
            // at least one action selected
            return sub.actions.some((act) => act.selected)
          }

          if (activeStatus === 'Deactive') {
            // all actions NOT selected
            return sub.actions.every((act) => !act.selected)
          }

          return true
        })

        if (filteredSubPermissions.length === 0) return null

        return {
          ...perm,
          subPermissions: filteredSubPermissions
        }
      })
      .filter((p): p is Permission => Boolean(p))

    setfilteredPermissions(result)
  }, [searchInputVal, activeStatus, temporaryRoleDetails])

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <InputWithLabel
          label={getTranslate('Role Name')}
          autoComplete="off"
          noHelperText
          fullWidth
          inputSize="small"
          disabled={readOnly}
          value={temporaryRoleDetails?.name}
          onChange={(event) =>
            setTemporaryRoleDetails({
              ...temporaryRoleDetails,
              name: event.currentTarget.value
            })
          }
        />
        <InputWithLabel
          label={getTranslate('Description')}
          autoComplete="off"
          noHelperText
          fullWidth
          inputSize="small"
          disabled={readOnly}
          value={temporaryRoleDetails?.description}
          onChange={(event) =>
            setTemporaryRoleDetails({
              ...temporaryRoleDetails,
              description: event.currentTarget.value
            })
          }
        />
        <SelectWithLabel
          label={getTranslate('Entity Type')}
          value={temporaryRoleDetails.entityType}
          onChange={(event) =>
            setTemporaryRoleDetails({
              ...temporaryRoleDetails,
              entityType: event.currentTarget.value as EntityType
            })
          }
          fullWidth
          noHelperText
          disabled={readOnly}
          size="small">
          {entityTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {getTranslate(option.label)}
            </MenuItem>
          ))}
        </SelectWithLabel>

        <FormControlLabel
          control={
            <Switch
              size="small"
              disabled={readOnly}
              checked={temporaryRoleDetails.isActive}
              onChange={(event) =>
                setTemporaryRoleDetails({
                  ...temporaryRoleDetails,
                  isActive: event.target.checked
                })
              }
              color="primary"
            />
          }
          label={getTranslate('Active')}
        />
      </div>
      <Divider className={classes.divider} />
      <div className={classes.row2}>
        <div>
          <Typography className={classes.title}>
            {getTranslate('Permissions')}
          </Typography>
          <Typography className={classes.subTitle}>
            {getTranslate('Configure what this role can access and modify')}
          </Typography>
        </div>
        <InputWithLabel
          placeholder={getTranslate('Search in role permissions')}
          autoComplete="off"
          noHelperText
          fullWidth
          startIcon={<SearchIcon />}
          inputSize="small"
          value={searchInputVal}
          onChange={(event) => setSearchInputVal(event.currentTarget.value)}
        />
      </div>
      <ActiveTabs
        title={getTranslate('Permission Status')}
        value={activeStatus}
        setValue={setaAtiveStatus}
      />
    </div>
  )
}

export default RoleBasicInfo
